<template>
  <div class="filter-chip">
    <div
      v-if="draggingOverLeft"
      class="_drag-over-left"
    />
    <m-dropdown
      v-if="!isQuickFilter"
      v-model:value="open"
      :title="$t('listFilter.title')"
      placement="bottomLeft"
    >
      <view-toolbar-chip-trigger
        :icon="chipIcon"
        :has-value="chipHasValue"
        :has-unsaved-changes="hasUnsavedChanges"
        :disabled="disabled"
      >
        {{ label }}
      </view-toolbar-chip-trigger>
      <template #overlay>
        <m-card
          class="_overlay"
          no-padding
        >
          <m-content
            class="_scope-filter"
            :padding-x="7"
            :padding-top="7"
            :padding-bottom="disabled ? 7 : 4"
            boxed-small
          >
            <m-tooltip
              placement="left"
              :disabled="!disabled"
              :mouse-enter-delay=".5"
            >
              <scope-filter
                :value="filter"
                :props="allowedProps"
                :account="account"
                :max-tag-text-length="15"
                :has-dynamic-dates="hasDynamicDates"
                :disabled="disabled"
                :multi-model="multiModel"
                :max-tags="maxTags"
                show-empty-info
                can-remove-all
                allow-grouping
                skip-validation
                @input="update"
              />
              <template #title>
                {{ $t('savedViewInfo.disabledInfo') }}
              </template>
            </m-tooltip>
          </m-content>
          <template v-if="!disabled">
            <m-divider none />
            <m-content
              padding-xxs
            >
              <m-card-item
                icon="delete"
                :disabled="disabled"
                :color="$colors.red.base"
                @click="update(null)"
              >
                {{ $t('filterChips.deleteFilter') }}
              </m-card-item>
            </m-content>
          </template>
        </m-card>
      </template>
    </m-dropdown>
    <quick-filter
      v-else
      v-model:open="open"
      :filter="filter"
      :scope-items="scopeItems"
      :read-only="disabled"
      :max-tag-text-length="15"
      :has-dynamic-dates="hasDynamicDates"
      @update:filter="update"
      @delete="update(null)"
      @add-to-advanced="emit('addToAdvanced')"
    >
      <view-toolbar-chip-trigger
        :icon="chipIcon"
        :has-value="chipHasValue"
        :has-unsaved-changes="hasUnsavedChanges"
        :clickable="!disabled"
        :disabled="disabled"
      >
        {{ label }}
      </view-toolbar-chip-trigger>
    </quick-filter>
    <div
      v-if="draggingOverRight"
      class="_drag-over-right"
    />
  </div>
</template>

<script setup>
import QuickFilter from '@/components/filter/quick/QuickFilter.vue';
import ScopeFilter from '@/components/filter/ScopeFilter.vue';
import ViewToolbarChipTrigger from '@/components/list/ViewToolbarChipTrigger.vue';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useScopeItem from '@/composables/filter/scope-item';
import { computed, ref, watch } from 'vue';
import { hasValueScope } from '@/lib/filter/scope-tree';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  name: { type: String, default: '' },
  filter: {
    type: Object,
    default: () => null,
  },
  props: {
    type: Array,
    required: true,
  },
  hasUnsavedChanges: {
    type: Boolean,
    default: false,
  },
  hasDynamicDates: {
    type: Boolean,
    default: false,
  },
  savedView: {
    type: Object,
    default: () => null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiModel: {
    type: Boolean,
    default: false,
  },
  scopeItems: {
    type: Array,
    default: () => [],
  },
  account: {
    type: Object,
    default: () => null,
  },
  autoOpen: {
    type: Boolean,
    default: false,
  },
  isQuickFilter: {
    type: Boolean,
    default: false,
  },
  draggingOverLeft: {
    type: Boolean,
    default: false,
  },
  draggingOverRight: {
    type: Boolean,
    default: false,
  },
  maxTags: {
    type: Number,
    default: 2,
  },
});

const emit = defineEmits(['update:filter', 'addToAdvanced']);

const { selectedScopeItem: getSelectedScopeItem, getLabel } = useScopeItem();
const { userLang } = useLoggedInUser();

const allowedProps = computed(() => props.props.filter((p) => !p.property.noFilter));

const selectedScopeItem = computed(() => getSelectedScopeItem(props.scopeItems, props.filter.children[0].children[0].scope));

const scope = computed(() => (props.filter.children?.length > 0 && props.filter.children[0].children?.length > 0
  ? props.filter.children[0].children[0].scope
  : null));

const chipIcon = computed(() => {
  if (props.isQuickFilter === true) {
    return selectedScopeItem.value.icon;
  }
  return 'funnel';
});

const chipHasValue = computed(() => {
  if (!props.isQuickFilter && props.filter.children.length > 0) {
    return true;
  }
  if (hasValueScope(scope.value)) {
    return true;
  }
  return false;
});

const filterLabelValue = computed(() => {
  let val = '';
  if (props.scopeItems != null && scope.value != null) {
    val = getLabel(props.scopeItems, scope.value, userLang.value, props.filter.children[0].op);
  }
  return val;
});

const label = computed(() => {
  if (props.isQuickFilter) {
    if (filterLabelValue.value.trim() !== '') {
      return `${selectedScopeItem.value.text}: ${filterLabelValue.value}`;
    }
    return selectedScopeItem.value.text;
  }
  return `${props.filter.children.length} ${t('filterChips.rule', { count: props.filter.children.length })}`;
});

const update = (value) => {
  emit('update:filter', value);
};

const open = ref(false);
const show = () => {
  open.value = true;
};

watch(() => props.autoOpen, (newVal) => {
  if (newVal) {
    show();
  }
});

if (props.autoOpen) {
  show();
}
defineExpose({ show });
</script>

<style scoped lang="scss" type="text/scss">
.filter-chip {
  position: relative;

  ._drag-over-left {
    position: absolute;
    top: 0;
    right: 0;
    left: -6px;
    z-index: 88;
    width:4px;
    height: 100%;
    pointer-events: none;
    background: $highlighted-color-dark;
    opacity: 1;
  }

  ._drag-over-right {
    position: absolute;
    right:-6px;
    top: 0;
    z-index: 88;
    width: 4px;
    height: 100%;
    pointer-events: none;
    background: $highlighted-color-dark;
    opacity: 1;
  }
}

._overlay {
  ._scope-filter {
    max-height: 50vh;
    overflow: auto;
  }
}
</style>
